import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { SalesInvoice } from '../../interfaces/sales/salesInvoice.interface';
import { Receipt, ReceiptCreateDTO, ReceiptMethod } from '../../interfaces/sales/receipt.interface';
import { ReceiptService } from '../../services/receipt.service';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { defaultReceiptMethodForAccount, depositAccountOptionsFor, foreignCurrencyNameHint } from '../../utils/postingAccounts';
import { ExchangeRateService } from '../../services/exchangeRate.service';
import { isForeignCurrency, formatFc } from '../../utils/fcDocuments';
import { AuthService } from '../../services/auth.service';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();

const RECEIPT_METHODS: { value: ReceiptMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_BANKING', label: 'Mobile Banking' },
  { value: 'OTHER', label: 'Other' },
];

interface Props {
  open: boolean;
  invoice: SalesInvoice;
  onClose: () => void;
  onPosted: (r: Receipt) => void;
}

const RecordReceiptDialog: React.FC<Props> = ({ open, invoice, onClose, onPosted }) => {
  const toast = useToast();

  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);

  // Form state
  const [amount, setAmount] = useState<number>(NaN);
  const [receiptDate, setReceiptDate] = useState(today());
  const [depositAccountId, setDepositAccountId] = useState('');
  const [method, setMethod] = useState<ReceiptMethod>('CASH');
  const [methodTouched, setMethodTouched] = useState(false);
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  // Computed outstanding
  const outstanding = invoice.totalAmount - invoice.paidAmount - (invoice.creditedAmount ?? 0);
  // ---- B4-C: foreign-currency invoice: the receipt states the FC amount and the rate of the day ----
  const currency = invoice.currencyCode ?? 'BDT';
  const foreign = isForeignCurrency(currency);
  const fcOutstanding = invoice.foreignOutstanding ?? 0;
  const [rate, setRate] = useState<number | ''>('');
  const [rateHint, setRateHint] = useState('');
  const rateTouched = useRef(false);
  // ---- end B4-C ----

  // Load ASSET accounts once on mount
  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form state when dialog opens (or when invoice changes mid-open)
  useEffect(() => {
    if (open) {
      setAmount(foreign ? (invoice.foreignOutstanding ?? 0) : invoice.totalAmount - invoice.paidAmount - (invoice.creditedAmount ?? 0));
      setRate('');
      rateTouched.current = false;
      setReceiptDate(today());
      setDepositAccountId('');
      setMethod('CASH');
      setMethodTouched(false);
      setReference('');
    }
  }, [open, invoice]);

  // B4-C: prefill the rate in force on the receipt date (editable)
  useEffect(() => {
    if (!open || !foreign || rateTouched.current) return;
    let live = true;
    ExchangeRateService.latest(AuthService.getSelectedInstitution() ?? invoice.institutionId, currency, receiptDate)
      .then(r => { if (live) { setRate(r.rate); setRateHint(`Rate of ${r.rateDate} from the exchange-rate table`); } })
      .catch(() => { if (live) { setRate(''); setRateHint(`No ${currency} rate on file for ${receiptDate}: enter the rate`); } });
    return () => { live = false; };
  }, [open, foreign, currency, receiptDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const limit = foreign ? fcOutstanding : outstanding;
  const amountValid = Number.isFinite(amount) && amount > 0 && amount <= limit + 0.00005;
  const submitDisabled = saving || !amountValid || !depositAccountId || (foreign && !(Number(rate) > 0));

  const handleSubmit = async () => {
    if (submitDisabled) return;

    setSaving(true);
    try {
      const dto: ReceiptCreateDTO = {
        salesInvoiceId: invoice.id,
        ...(foreign ? { foreignAmount: amount, exchangeRate: Number(rate) } : { amount }),
        receiptDate,
        method,
        reference: reference.trim() || undefined,
        depositAccountId,
      };
      const receipt = await ReceiptService.create(dto);
      toast.showSuccess('Receipt recorded successfully');
      onPosted(receipt);
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to record receipt');
      // Keep dialog open so user can correct the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-help-page="sales.recordReceipt.page">
      <DialogTitle>Record Receipt</DialogTitle>
      <DialogContent data-help-section="sales.recordReceipt.section.form">
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* Outstanding summary */}
          <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Typography variant="body2">Invoice Total: {formatMoney(invoice.totalAmount)}</Typography>
            <Typography variant="body2">Paid: {formatMoney(invoice.paidAmount)}</Typography>
            <Typography variant="body2" fontWeight="bold">
              Outstanding: {formatMoney(outstanding)}
            </Typography>
            {foreign && (
              <Typography variant="body2" fontWeight="bold">
                Outstanding in {currency}: {formatFc(currency, fcOutstanding)}
              </Typography>
            )}
          </Box>

          {/* Amount */}
          <TextField
            data-help="sales.recordReceipt.amount"
            label={foreign ? `Amount (${currency})` : 'Amount'}
            type="number"
            value={Number.isFinite(amount) ? amount : ''}
            onChange={(e) =>
              setAmount(e.target.value === '' ? NaN : Number(e.target.value))
            }
            inputProps={{ min: 0, max: limit, step: 'any' }}
            fullWidth
            required
            error={Number.isFinite(amount) && !amountValid}
            helperText={
              Number.isFinite(amount) && !amountValid
                ? `Amount must be between 0 and ${foreign ? formatFc(currency, fcOutstanding) : formatMoney(outstanding)}`
                : undefined
            }
          />

          {foreign && (
            <TextField
              data-help="sales.recordReceipt.rate"
              label={`Rate (BDT per ${currency})`}
              type="number"
              value={rate}
              onChange={(e) => { rateTouched.current = true; setRate(e.target.value === '' ? '' : Number(e.target.value)); }}
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              required
              helperText={
                Number(rate) > 0 && Number.isFinite(amount)
                  ? `${rateHint ? rateHint + '. ' : ''}= BDT ${formatMoney(amount * Number(rate))} received`
                  : rateHint || undefined
              }
            />
          )}

          {/* Receipt Date */}
          <TextField
            data-help="sales.recordReceipt.date"
            label="Receipt Date"
            type="date"
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          {/* Deposit Account (ASSET accounts) */}
          <AccountAutocomplete
            data-help="sales.recordReceipt.depositAccount"
            accounts={depositAccountOptionsFor(assetAccounts, currency)}
            value={depositAccountId}
            onChange={(v: string) => {
              setDepositAccountId(v);
              if (!methodTouched) setMethod(defaultReceiptMethodForAccount(assetAccounts.find(a => a.id === v)));
            }}
            label="Deposit Account"
            required
            helperText={foreignCurrencyNameHint(assetAccounts.find(a => a.id === depositAccountId)) ?? undefined}
            error={!!foreignCurrencyNameHint(assetAccounts.find(a => a.id === depositAccountId))}
          />

          {/* Payment Method */}
          <TextField
            data-help="sales.recordReceipt.method"
            select
            label="Method"
            value={method}
            onChange={(e) => { setMethodTouched(true); setMethod(e.target.value as ReceiptMethod); }}
            fullWidth
            required
          >
            {RECEIPT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Reference */}
          <TextField
            data-help="sales.recordReceipt.reference"
            label="Reference (optional)"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.recordReceipt.cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          data-help="sales.recordReceipt.submit"
          onClick={handleSubmit}
          variant="contained"
          disabled={submitDisabled}
        >
          {saving ? 'Saving…' : 'Record Receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordReceiptDialog;
